<template>
  <component
    :is="cardless ? 'div' : 'base-card'"
    title="Daftar Faktur Pembayaran"
  >
    <datatable compact :columns="tableColumns">
      <template v-slot:tbody="{ classes }">
        <template v-for="payment in payments.data">
          <tr :key="payment.id" :class="[classes.tr]">
            <td :class="[classes.td]">
              <span class="font-bold text-gray-900">
                {{ payment.attributes.code }}
              </span>
            </td>
            <td :class="[classes.td]">
              {{ payment.attributes.destination_office_code }} /
              {{ payment.attributes.origin_warehouse_code }}
            </td>
            <td :class="[classes.td]">
              {{ payment.attributes.createdAt | formatDate }}
            </td>
            <td v-if="!readOnly" colspan="2"></td>
            <td :class="[classes.td, 'text-right']">
              {{
                payment.attributes[
                  orderValid ? 'cashier_total_amount' : 'total_amount'
                ] | toCurrency
              }}
            </td>
            <td
              v-if="columns.includes('state')"
              :class="[classes.td, 'text-center']"
              :title="getPaymentTooltip(payment)"
            >
              <svg
                v-if="checkPaymentIsVerified(payment)"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="mx-auto h-5 text-green-500"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <button
                v-else-if="checkPaymentIsPending(payment)"
                title="Metode pembayaran harus diverifikasi terlebih dahulu"
                @click="onCollapse(payment)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="mx-auto h-5 w-5 text-yellow-500"
                >
                  <path
                    fill-rule="evenodd"
                    d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
              <base-button
                v-else-if="checkAllowedToAction(payment)"
                size="sm"
                @click="onVerifyPayment(payment)"
                >Verifikasi</base-button
              >
            </td>
            <td :class="[classes.td, 'text-right']">
              <button class="text-right" @click="onCollapse(payment)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="mx-auto h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  v-if="collapsedId === payment.id"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 15l7-7 7 7"
                  />
                </svg>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  v-else
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </td>
          </tr>
          <template v-if="collapsedId === payment.id">
            <tr
              v-for="paymentMethod in usedPaymentMethods"
              :key="`${payment.id}-${paymentMethod.id}`"
              :class="[
                classes.tr,
                paymentMethod.attributes.canceled ? 'bg-red-50' : 'bg-gray-50',
              ]"
            >
              <td :class="[classes.td]">
                <template
                  v-if="
                    !readOnly &&
                    orderValid &&
                    !paymentMethod.attributes.is_verified &&
                    !paymentMethod.attributes.canceled &&
                    checkPaymentMethodRequireUpload(paymentMethod) &&
                    checkAllowedToAction(paymentMethod)
                  "
                >
                  <a
                    v-if="paymentMethod.attributes.url"
                    :href="paymentMethod.attributes.url"
                  >
                    <base-button size="sm" color="white">
                      <Icon icon="heroicons:arrow-down-tray" />
                      Download Bukti
                    </base-button>
                  </a>
                  <base-button
                    v-else
                    size="sm"
                    color="white"
                    @click="onOpenUploadFile(paymentMethod)"
                  >
                    <Icon icon="heroicons:arrow-up-tray" />
                    Upload Bukti
                  </base-button>
                </template>
              </td>
              <td
                :class="[
                  classes.td,
                  paymentMethod.attributes.canceled
                    ? 'text-red-600 line-through'
                    : '',
                ]"
              >
                <div
                  :class="[
                    'flex items-center gap-2',
                    readOnly ? 'text-xs' : '',
                  ]"
                >
                  <p v-if="paymentMethod.attributes.balance_used === 'restock'">
                    Saldo Restock
                  </p>
                  <p
                    v-else-if="
                      paymentMethod.attributes.balance_used ===
                      'multiplied_deposit'
                    "
                  >
                    Saldo Nilai Barang Dari Deposit
                  </p>
                  <template v-else>
                    <p>{{ paymentMethod.attributes.payment_method_type }}</p>
                    <p
                      v-if="
                        paymentMethod.attributes.office_bank_name &&
                        (readOnly ||
                          !checkPaymentMethodRequireStruct(paymentMethod))
                      "
                    >
                      - {{ paymentMethod.attributes.office_bank_name }}
                    </p>
                    <BaseButton
                      v-if="
                        paymentMethod.attributes.payment_method_type === 'QRIS'
                      "
                      color="white"
                      size="sm"
                      @click="onOpenPaymentMethodQr(paymentMethod)"
                    >
                      <Icon icon="heroicons:qr-code-solid" class="h-4 w-4" />
                    </BaseButton>
                    <template
                      v-if="checkPaymentMethodRequireStruct(paymentMethod)"
                    >
                      <select-payment-method-bank
                        v-if="!readOnly && !paymentMethod.attributes.canceled"
                        :payment-method-id="paymentMethod.id"
                        :office-banks="officeBanks.data"
                        :disabled="
                          paymentMethod.attributes.is_verified ||
                          !checkAllowedToAction(paymentMethod)
                        "
                        v-model="paymentMethod.attributes.office_bank_id"
                        @change="onChangeBank(paymentMethod)"
                      />
                      <p
                        v-if="
                          readOnly && paymentMethod.attributes.receipt_number
                        "
                      >
                        ({{ paymentMethod.attributes.receipt_number }})
                      </p>
                    </template>
                  </template>

                  <Icon v-if="viewAs === 'order' && paymentMethod.attributes.is_verified" icon="heroicons:shield-check-solid" class="text-green-600" />
                </div>
              </td>
              <td
                :class="[
                  classes.td,
                  paymentMethod.attributes.canceled
                    ? 'text-red-600 line-through'
                    : '',
                  readOnly ? 'text-xs' : '',
                ]"
              >
                {{ paymentMethod.attributes.updatedAt | formatDate }}
              </td>
              <td
                v-if="!readOnly"
                :class="[
                  classes.td,
                  paymentMethod.attributes.canceled
                    ? 'text-red-600 line-through'
                    : '',
                ]"
              >
                <base-input
                  v-if="
                    paymentMethod.attributes.office_bank_type === 'bank' &&
                    checkAllowedToAction(paymentMethod)
                  "
                  size="sm"
                  type="date"
                  :shadow="false"
                  :disabled="paymentMethod.attributes.is_verified"
                  v-model="paymentMethod.attributes.transfer_date"
                  @change="onChangeTransferDate(paymentMethod)"
                />
              </td>
              <td v-if="!readOnly" :class="[classes.td]">
                <base-input
                  v-if="
                    !paymentMethod.attributes.canceled &&
                    orderValid &&
                    checkPaymentMethodRequireStruct(paymentMethod)
                  "
                  type="text"
                  size="sm"
                  :shadow="false"
                  placeholder="Masukkan no. struk"
                  v-model="paymentMethod.attributes.receipt_number"
                  :disabled="
                    paymentMethod.attributes.is_verified ||
                    !checkAllowedToAction(paymentMethod)
                  "
                  @change="onChangeReceiptNumber(paymentMethod)"
                />
              </td>
              <td
                :class="[
                  classes.td,
                  'text-right',
                  paymentMethod.attributes.canceled
                    ? 'text-red-600 line-through'
                    : '',
                  readOnly ? 'text-xs' : '',
                ]"
              >
                <p v-if="!orderValid">
                  {{ paymentMethod.attributes.payment_amount | toCurrency }}
                </p>
                <div v-else class="flex justify-end">
                  <base-input
                    v-if="
                      editable &&
                      editPaymentMethod.visible &&
                      editPaymentMethod.id === paymentMethod.id
                    "
                    ref="editPaymentMethodInput"
                    currency
                    type="text"
                    placeholder="Nominal pembayaran"
                    v-model="editPaymentMethod.value"
                  />
                  <p v-else>
                    {{
                      paymentMethod.attributes.cashier_payment_amount
                        | toCurrency
                    }}
                    <span
                      v-if="
                        editable &&
                        payment.attributes.is_first_payment &&
                        paymentMethod.attributes.created_by_type !==
                          'cashier' &&
                        paymentMethod.attributes.cashier_payment_amount !==
                          paymentMethod.attributes.payment_amount
                      "
                      class="tooltip text-red-600"
                    >
                      *
                      <span class="tooltiptext"
                        >Nominal awal
                        {{
                          paymentMethod.attributes.payment_amount | toCurrency
                        }}</span
                      >
                    </span>
                  </p>
                </div>
              </td>
              <td
                v-if="columns.includes('state')"
                :class="[classes.td, 'text-center']"
                :title="getPaymentMethodTooltip(paymentMethod)"
              >
                <svg
                  v-if="checkPaymentMethodIsAllVerified(paymentMethod)"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="mx-auto h-5 w-5 text-green-500"
                >
                  <path
                    fill-rule="evenodd"
                    d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                    clip-rule="evenodd"
                  ></path>
                </svg>
                <svg
                  v-else-if="
                    checkPaymentMethodIsWaitForEdcVerified(paymentMethod)
                  "
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="mx-auto h-5 w-5 text-yellow-500"
                >
                  <path
                    fill-rule="evenodd"
                    d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                    clip-rule="evenodd"
                  />
                </svg>
                <div
                  v-else-if="checkAllowedToAction(paymentMethod)"
                  class="flex items-center justify-center gap-1"
                >
                  <base-button
                    v-if="paymentMethod.attributes.canceled"
                    size="sm"
                    color="white"
                    @click="onRestorePaymentMethod(paymentMethod.id)"
                    >Restore</base-button
                  >
                  <template v-else>
                    <template
                      v-if="
                        !editPaymentMethod.visible ||
                        editPaymentMethod.id !== paymentMethod.id
                      "
                    >
                      <svg
                        v-if="!paymentMethod.attributes.valid_for_verify"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="mx-auto h-5 w-5 text-yellow-500"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                          clip-rule="evenodd"
                        />
                      </svg>
                      <base-button
                        v-else
                        size="sm"
                        @click="onVerifyPaymentMethod(paymentMethod)"
                        >Verifikasi</base-button
                      >
                      <template
                        v-if="editable && !paymentMethod.attributes.is_verified"
                      >
                        <base-button
                          size="sm"
                          color="white"
                          @click="onEditPaymentMethod(paymentMethod.id)"
                        >
                          <Icon
                            icon="heroicons:pencil-square-solid"
                            class="h-4 w-4"
                          />
                        </base-button>
                        <base-button
                          size="sm"
                          color="white"
                          @click="onDeletePaymentMethod(paymentMethod.id)"
                        >
                          <Icon icon="heroicons:trash-solid" class="h-4 w-4" />
                        </base-button>
                      </template>
                    </template>

                    <template v-if="editPaymentMethod.id === paymentMethod.id">
                      <base-button
                        size="sm"
                        color="white"
                        @click="onCancelEditPaymentMethod"
                      >
                        <Icon icon="heroicons:x-mark" class="h-4 w-4" />
                        Batal
                      </base-button>
                      <base-button size="sm" @click="onSubmitEditPaymentMethod">
                        <Icon icon="heroicons:check" class="h-4 w-4" />
                        Simpan
                      </base-button>
                    </template>
                  </template>
                </div>
              </td>
              <td></td>
            </tr>
            <tr
              v-if="editable && !payment.attributes.is_verified && allowedToAdd"
              :key="`${payment.id}-add`"
              :class="[classes.tr, 'bg-gray-50']"
            >
              <td :class="classes.td"></td>
              <td :class="classes.td">
                <button
                  class="flex items-center"
                  @click="onCreatePaymentMethod(payment)"
                >
                  <Icon
                    icon="heroicons:plus-circle"
                    class="mx-auto h-6 w-6 text-green-600 hover:text-green-500"
                  />
                </button>
              </td>
              <td colspan="6"></td>
            </tr>
          </template>
        </template>
      </template>
      <template v-if="summary" v-slot:tfoot="{ classes }">
        <tfoot>
          <tr :class="classes.tr">
            <td :colspan="!withTotalPaymentAmount || !withTotalPaymentRemainder ? 3 : 4"></td>
            <td v-if="!withTotalPaymentAmount || !withTotalPaymentRemainder">
              <p v-if="accPaymentReason" class="font-semibold text-gray-900">Acc Atasan: {{ accPaymentReason }}</p>
            </td>
            <td :class="[classes.td]">Total Penjualan</td>
            <td :class="[classes.td, 'text-right']">
              {{ orderPrice | toCurrency }}
            </td>
          </tr>
          <tr v-if="withTotalPaymentAmount" :class="classes.tr">
            <td :colspan="!withTotalPaymentRemainder ? 3 : 4"></td>
            <td v-if="!withTotalPaymentRemainder" :class="[classes.td, 'pt-0']">
              <p v-if="accPaymentReason" class="font-semibold text-gray-900">Acc Atasan: {{ accPaymentReason }}</p>
            </td>
            <td :class="[classes.td, 'pt-0']">Total Pembayaran</td>
            <td :class="[classes.td, 'pt-0 text-right']">
              {{ paymentAmount | toCurrency }}
            </td>
          </tr>
          <tr v-if="withTotalPaymentRemainder" :class="classes.tr">
            <td :colspan="withTotalPaymentRemainder ? 3 : 4"></td>
            <td v-if="withTotalPaymentRemainder" :class="[classes.td, 'pt-0']">
              <p v-if="accPaymentReason" class="font-semibold text-gray-900">Acc Atasan: {{ accPaymentReason }}</p>
            </td>
            <td :class="[classes.td, 'pt-0']">Total Kekurangan</td>
            <td :class="[classes.td, 'pt-0 text-right']">
              {{ Math.max(orderPrice - paymentAmount, 0) | toCurrency }}
            </td>
          </tr>
        </tfoot>
      </template>
    </datatable>
    <create-payment-method-modal
      v-if="editable"
      :order-id="orderId"
      :payment-id="createPaymentMethod.paymentId"
      :office-bank-office-id="officeBankId || me.office_id"
      :visible="createPaymentMethod.visible"
      :create-payment-form-props="{
        ...createPaymentFormProps,
        filterReserved: hasCashback,
      }"
      @close="createPaymentMethod.visible = false"
      @created="loadPaymentMethods"
    />
    <BaseQrisPaymentModal :visible="showingPaymentMethodQr.visible" :detail="showingPaymentMethodQr" @close="showingPaymentMethodQr.visible = false" />
    <teleport to="body">
      <base-modal
        :showing="uploadFile.visible"
        @close="uploadFile.visible = false"
      >
        <base-card title="Bukti File" :with-style="false">
          <form class="space-y-4" @submit.prevent="onUploadFile">
            <div>
              <base-input
                type="file"
                :shadow="false"
                fullwidth
                size="padding-md"
                label="Upload File Bukti"
                @change="onChangeUploadFile"
              />
              <p class="mt-1 text-xs text-gray-500">Tipe file: jpg, png</p>
            </div>
            <div class="flex gap-2">
              <base-button>Upload</base-button>
              <base-button
                type="button"
                color="white"
                @click="uploadFile.visible = false"
                >Tutup</base-button
              >
            </div>
          </form>
        </base-card>
      </base-modal>
    </teleport>
    <teleport to="body">
      <loading v-if="loading" />
    </teleport>
  </component>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import Teleport from 'vue2-teleport';
import { toCurrency } from '@/services/currency.service';
import { mapActions } from 'vuex';
import CreatePaymentMethodModal from './create-payment-method-modal.vue';
import BaseModal from '@/components/base/BaseModal.vue';
import selectPaymentMethodBank from './select-payment-method-bank.vue';
import { mapGetters } from 'vuex';
import QrCode from 'vue-qrcode-component';
import BaseQrisPaymentModal from '../base/BaseQrisPaymentModal.vue';

export default {
  mixins: [requestMixin],
  components: {
    Teleport,
    CreatePaymentMethodModal,
    BaseModal,
    selectPaymentMethodBank,
    QrCode,
    BaseQrisPaymentModal
  },
  props: {
    viewAs: String,
    createPaymentFormProps: Object,
    readOnly: Boolean,
    orderId: String,
    orderValid: Boolean,
    orderCode: {
      type: String,
      required: true,
    },
    orderPaid: Boolean,
    payments: Object,
    orderPrice: Number,
    cardless: Boolean,
    officeBankId: null,
    columns: {
      type: Array,
      default: () => ['state'],
    },
    withTotalPaymentAmount: Boolean,
    withTotalPaymentRemainder: Boolean,
    withUnused: Boolean,
    paymentAmount: Number,
    editable: Boolean,
    summary: {
      type: Boolean,
      default: true,
    },
    originOfficeId: String,
    originOfficeType: String,
    paymentMethodRequestParams: null,
    accPaymentReason: null
  },
  emits: ['reload', 'verified'],
  data() {
    return {
      collapsedId: null,
      loadingPaymentMethods: false,
      loadingVerifyPayment: false,
      loadingVerifyPaymentMethod: false,
      loadingEditPaymentMethod: false,
      loadingDeletePaymentMethod: false,
      loadingRestorePaymentMethod: false,
      loadingUploadFile: false,
      loadingOfficeBanks: false,
      officeBanks: {
        data: [],
      },
      paymentMethods: {
        data: [],
      },
      editPaymentMethod: {
        visible: false,
        id: null,
        value: null,
      },
      createPaymentMethod: {
        visible: false,
        paymentId: null,
      },
      uploadFile: {
        visible: false,
        paymentMethodId: null,
        file: null,
      },
      showingPaymentMethodQr: {
        visible: false,
        qris: null,
        price: null,
        qris_merchant: null,
        qris_city: null,
        bank_account_name: null,
        bank_account_number: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    hasCashback() {
      return this.paymentMethods.data.some(item => item.attributes.balance_used === 'cashback' && !item.attributes.canceled)
    },
    tableColumns() {
      return [
        { id: 'code', name: 'No Pembayaran' },
        { id: 'warehouse_code', name: 'Kode Gudang' },
        { id: 'date', name: 'Tanggal' },
        this.readOnly
          ? null
          : { id: 'transfer_date', name: 'Tanggal Transfer' },
        this.readOnly ? null : { id: 'struct', name: 'Nomor Struk' },
        { id: 'amount', name: 'Total', theadClass: 'text-right' },
        this.columns.includes('state')
          ? { id: 'state', name: '', theadClass: 'text-center' }
          : null,
        { id: 'action', name: '', theadClass: 'text-right' },
      ].filter((i) => i !== null);
    },
    loading() {
      return (
        this.loadingPaymentMethods ||
        this.loadingVerifyPayment ||
        this.loadingVerifyPaymentMethod ||
        this.loadingEditPaymentMethod ||
        this.loadingDeletePaymentMethod ||
        this.loadingRestorePaymentMethod ||
        this.loadingUploadFile ||
        this.loadingOfficeBanks
      );
    },
    usedPaymentMethods() {
      if (!this.withUnused) {
        return this.paymentMethods.data.filter(
          (item) => item.attributes.is_used
        );
      }

      return this.paymentMethods.data;
    },
    allowedToAdd() {
      if (!this.originOfficeId) {
        return true;
      }

      return this.me.office_id === this.originOfficeId;
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    checkAllowedToAction(paymentMethod) {
      if (paymentMethod.type === 'payments') {
        if (!this.originOfficeId) {
          return true;
        }

        return this.me.office_id === this.originOfficeId;
      }

      return paymentMethod.attributes.current_user_allow_edit;
    },
    checkPaymentMethodRequireUpload(paymentMethod) {
      return (
        paymentMethod.attributes.payment_method_type === 'Transfer Bank' ||
        paymentMethod.attributes.payment_method_type === 'EDC' ||
        paymentMethod.attributes.payment_method_type === 'QRIS'
      );
    },
    checkPaymentMethodRequireStruct(paymentMethod) {
      return paymentMethod.attributes.payment_method_type === 'EDC';
    },
    checkPaymentMethodIsAllVerified(paymentMethod) {
      if (!paymentMethod.attributes.is_verified) {
        return false;
      }

      const isOriginKp = this.originOfficeType === 'kantor_pelayanan';

      if (
        paymentMethod.attributes.payment_method_type !== 'EDC' ||
        !isOriginKp
      ) {
        return true;
      }

      return paymentMethod.attributes.is_edc_verified;
    },
    checkPaymentIsVerified(payment) {
      if (!payment.attributes.is_verified) {
        return false;
      }

      if (this.originOfficeType !== 'kantor_pelayanan') {
        return true;
      }

      return this.orderPaid;
    },
    checkPaymentIsPending(payment) {
      if (payment.attributes.state === 'pending') {
        return true;
      }

      if (this.originOfficeType !== 'kantor_pelayanan') {
        return false;
      }

      if (!payment.attributes.is_verified) {
        return false;
      }

      return !this.orderPaid;
    },
    checkPaymentMethodIsWaitForEdcVerified(paymentMethod) {
      if (!paymentMethod.attributes.is_verified) {
        return false;
      }

      if (paymentMethod.attributes.is_edc_verified) {
        return false;
      }

      if (paymentMethod.attributes.payment_method_type !== 'EDC') {
        return false;
      }

      if (this.originOfficeType !== 'kantor_pelayanan') {
        return false;
      }

      if (this.me.office_type === 'pusat') {
        return false;
      }

      return true;
    },
    getPaymentTooltip(payment) {
      if (payment.attributes.is_verified) {
        return '';
      }

      if (this.originOfficeType !== 'kantor_pelayanan') {
        return `Diverifikasi oleh ${payment.attributes.verified_by}`;
      }

      return this.orderPaid;
    },
    getPaymentMethodTooltip(paymentMethod) {
      if (!paymentMethod.attributes.is_verified) {
        return '';
      }

      const isOriginKp = this.originOfficeType === 'kantor_pelayanan';

      if (
        paymentMethod.attributes.payment_method_type !== 'EDC' ||
        !isOriginKp
      ) {
        return `Diverifikasi oleh ${paymentMethod.attributes.verified_by}`;
      }

      if (paymentMethod.attributes.is_edc_verified) {
        return 'Diverifikasi pusat';
      }

      if (this.me.office_type !== 'pusat') {
        return 'Belum diverifikasi pusat';
      }

      return '';
    },
    async loadOfficeBanks() {
      this.loadingOfficeBanks = true;
      const [res, err] = await this.request(
        `/api/v1/offices/${
          this.officeBankId || this.me.office_id
        }/office-banks`,
        {
          params: {
            'filter[office_bank_type]': 'bank',
            'fields[office-banks]': 'bank_name',
          },
        }
      );

      if (!err) {
        this.officeBanks = res;
      }
      this.loadingOfficeBanks = false;
    },
    async loadPaymentMethods() {
      this.loadingPaymentMethods = true;

      const [res, error] = await this.request(
        `/api/v1/payments/${this.collapsedId}/payment-methods`,
        {
          params: {
            'fields[payment-methods]':
              'qris,qris_merchant,qris_city,office_bank_account_name,office_bank_account_number,office_bank_image_url,verified_by,url,valid_for_verify,updatedAt,created_by_type,payment_method_type,office_bank_id,office_bank_name,cashier_payment_amount,payment_amount,is_verified,is_edc_verified,balance_used,is_used,canceled,current_user_allow_edit,receipt_number,transfer_date,office_bank_type',
            sort: 'canceled_at',
            ...this.paymentMethodRequestParams,
          },
        }
      );

      if (!error) {
        this.paymentMethods = res;
      }

      this.loadingPaymentMethods = false;
    },
    onCollapse(payment) {
      if (this.collapsedId === payment.id) {
        this.collapsedId = null;
      } else {
        this.collapsedId = payment.id;

        this.editPaymentMethod.id = null;
        this.editPaymentMethod.value = null;
        this.editPaymentMethod.visible = false;

        this.paymentMethods = {
          data: [],
        };

        this.loadPaymentMethods();
      }
    },
    async onVerifyPayment(payment) {
      this.loadingVerifyPayment = true;

      const [, error] = await this.request(
        `/api/v1/payments/${payment.id}/-actions/verify`,
        {
          method: 'post',
        }
      );

      if (!error) {
        this.$emit('verified');
      }

      this.loadingVerifyPayment = false;
    },
    async onChangeReceiptNumber(paymentMethod) {
      this.loadingEditPaymentMethod = true;

      await this.request(`/api/v1/payment-methods/${paymentMethod.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'payment-methods',
            id: paymentMethod.id,
            attributes: {
              receipt_number: paymentMethod.attributes.receipt_number,
            },
          },
        },
      });

      this.loadPaymentMethods();

      this.loadingEditPaymentMethod = false;
    },
    async onChangeBank(paymentMethod) {
      this.loadingEditPaymentMethod = true;

      await this.request(`/api/v1/payment-methods/${paymentMethod.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'payment-methods',
            id: paymentMethod.id,
            relationships: {
              'office-bank': {
                data: {
                  type: 'office-banks',
                  id: paymentMethod.attributes.office_bank_id,
                },
              },
            },
          },
        },
      });

      this.loadPaymentMethods();

      this.loadingEditPaymentMethod = false;
    },
    async onChangeTransferDate(paymentMethod) {
      this.loadingEditPaymentMethod = true;

      await this.request(`/api/v1/payment-methods/${paymentMethod.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'payment-methods',
            id: paymentMethod.id,
            attributes: {
              transfer_date: paymentMethod.attributes.transfer_date,
            },
          },
        },
      });

      this.loadingEditPaymentMethod = false;
    },
    async onVerifyPaymentMethod(paymentMethod) {
      this.loadingVerifyPaymentMethod = true;

      const [, error] = await this.request(
        `/api/v1/payment-methods/${paymentMethod.id}/-actions/${
          paymentMethod.attributes.is_verified ? 'verify-edc' : 'verify'
        }`,
        {
          method: 'post',
        }
      );

      if (!error) {
        await this.loadPaymentMethods();

        if (
          this.paymentMethods.data.filter(
            (paymentMethod) =>
              !paymentMethod.attributes.canceled &&
              !paymentMethod.attributes.is_verified
          ).length < 1
        ) {
          this.$emit('reload');
        }
      }

      this.loadingVerifyPaymentMethod = false;
    },
    async onEditPaymentMethod(id) {
      const index = this.paymentMethods.data.findIndex(
        (paymentMethod) => paymentMethod.id === id
      );

      this.editPaymentMethod.id = id;
      this.editPaymentMethod.value = toCurrency(
        this.paymentMethods.data[index].attributes.cashier_payment_amount
      );
      this.editPaymentMethod.visible = true;

      await this.$nextTick();

      this.$refs.editPaymentMethodInput[0].$refs.input.focus();
    },
    onCancelEditPaymentMethod() {
      this.editPaymentMethod.id = null;
      this.editPaymentMethod.value = null;
      this.editPaymentMethod.visible = false;
    },
    async onSubmitEditPaymentMethod() {
      this.loadingEditPaymentMethod = true;

      const amount = Number(this.editPaymentMethod.value.replace(/\D/gi, ''));

      if (amount < 1) {
        this.createAlert({
          status: 'error',
          data: 'Nominal pembayaran minimal 1',
        });
      } else {
        const [res, err] = await this.request(
          `/api/v1/payment-methods/${this.editPaymentMethod.id}`,
          {
            method: 'patch',
            data: {
              data: {
                type: 'payment-methods',
                id: this.editPaymentMethod.id,
                attributes: {
                  cashier_payment_amount: amount,
                },
              },
            },
          }
        );

        if (err) {
          this.createAlert({
            status: 'error',
            data: this.getRequestErrorMessage(err),
          });
        } else {
          const index = this.paymentMethods.data.findIndex(
            (paymentMethod) => paymentMethod.id === this.editPaymentMethod.id
          );

          this.paymentMethods.data[index].attributes.cashier_payment_amount =
            res.data.attributes.cashier_payment_amount;

          this.editPaymentMethod.id = null;
          this.editPaymentMethod.value = null;
          this.editPaymentMethod.visible = false;

          this.$emit('reload');
        }
      }

      this.loadingEditPaymentMethod = false;
    },
    async onDeletePaymentMethod(id) {
      this.loadingDeletePaymentMethod = true;

      const [, err] = await this.request(
        `/api/v1/payment-methods/${id}/-actions/cancel`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.loadPaymentMethods();
        this.$emit('reload');
      }

      this.loadingDeletePaymentMethod = false;
    },
    async onRestorePaymentMethod(id) {
      this.loadingRestorePaymentMethod = true;

      const [, err] = await this.request(
        `/api/v1/payment-methods/${id}/-actions/restore`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.loadPaymentMethods();
        this.$emit('reload');
      }

      this.loadingRestorePaymentMethod = false;
    },
    onCreatePaymentMethod(payment) {
      this.createPaymentMethod.paymentId = payment.id;
      this.createPaymentMethod.visible = true;
    },
    onOpenUploadFile(paymentMethod) {
      this.uploadFile.paymentMethodId = paymentMethod.id;
      this.uploadFile.visible = true;
    },
    onChangeUploadFile(e) {
      this.uploadFile.file = e.target.files[0];
    },
    async onUploadFile() {
      this.loadingUploadFile = true;

      const data = new FormData();

      data.append('payment_method_id', this.uploadFile.paymentMethodId);
      data.append('url', this.uploadFile.file);

      const [, err] = await this.request(
        '/api/v1/payment-methods/-actions/uploadFiles',
        {
          method: 'post',
          data,
        }
      );

      if (!err) {
        this.loadPaymentMethods();
        this.uploadFile.visible = false;
      }

      this.loadingUploadFile = false;
    },
    onOpenPaymentMethodQr(paymentMethod) {
      this.showingPaymentMethodQr.visible = true;
      this.showingPaymentMethodQr.qris = paymentMethod.attributes.qris;
      this.showingPaymentMethodQr.qris_merchant =
        paymentMethod.attributes.qris_merchant;
      this.showingPaymentMethodQr.qris_city =
        paymentMethod.attributes.qris_city;
      this.showingPaymentMethodQr.bank_account_name =
        paymentMethod.attributes.office_bank_account_name;
      this.showingPaymentMethodQr.bank_account_number =
        paymentMethod.attributes.office_bank_account_number;
      this.showingPaymentMethodQr.price = this.orderValid
        ? paymentMethod.attributes.cashier_payment_amount
        : paymentMethod.attributes.payment_amount;
      this.showingPaymentMethodQr.orderCode = this.orderCode
    },
  },
  created() {
    if (!this.readOnly) {
      this.loadOfficeBanks();
    }
  },
};
</script>
